/**
 * Utility để lấy token từ global token store (memory)
 */
import tokenStore from './tokenStore';
import { clearSessionHint, isPublicBrowsePath } from './authRoutes';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const getAuthToken = () => {
    return tokenStore.getToken();
};

/**
 * Lưu access token vào global token store (memory)
 */
export const setAccessToken = (token) => {
    if (token) {
        tokenStore.setToken(token);
    }
};

/**
 * Xóa access token từ global token store
 */
export const clearAccessToken = () => {
    tokenStore.clearToken();
};

/**
 * Xóa tokens khi logout
 */
export const clearTokens = () => {
    tokenStore.clearToken();
    localStorage.removeItem('refreshToken');
    clearSessionHint();
};

export const clearStaleSession = async () => {
    clearAccessToken();
    clearTokens();

    try {
        await fetch(`${API_BASE}/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    } catch (_) {
        // Logged-out state is fine even if logout fails.
    }
};

/**
 * Tạo header Authorization nếu có token
 */
export const getAuthHeader = () => {
    const token = getAuthToken();
    if (!token) {
        return {};
    }
    return { Authorization: `Bearer ${token}` };
};

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach((callback) => callback(newToken));
    refreshSubscribers = [];
};

const redirectToLogin = () => {
    const path = window.location?.pathname || '';
    if (isPublicBrowsePath(path)) return;
    window.location.href = '/login';
};

const refreshAccessToken = async () => {
    const refreshResponse = await fetch(`${API_BASE}/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    });

    if (!refreshResponse.ok) {
        if (refreshResponse.status === 400 || refreshResponse.status === 401) {
            await clearStaleSession();
        }
        return null;
    }

    const data = await refreshResponse.json().catch(() => ({}));
    if (!data?.success || !data?.token) {
        return null;
    }

    setAccessToken(data.token);
    return data.token;
};

export const fetchWithAuth = async (url, options = {}) => {
    const authHeader = getAuthHeader();

    const headers = {
        ...(options.headers || {}),
        ...authHeader,
    };

    const baseReqInit = { ...options, headers };
    if (typeof baseReqInit.credentials === 'undefined') {
        baseReqInit.credentials = 'include';
    }

    let response = await fetch(url, baseReqInit);

    if (response.status === 401 && !url.includes('/refresh-token')) {
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                subscribeTokenRefresh((newToken) => {
                    if (!newToken) {
                        reject(new Error('SESSION_EXPIRED'));
                        return;
                    }
                    const newHeaders = {
                        ...(options.headers || {}),
                        Authorization: `Bearer ${newToken}`,
                    };
                    const retryInit = { ...options, headers: newHeaders };
                    if (typeof retryInit.credentials === 'undefined') {
                        retryInit.credentials = 'include';
                    }
                    fetch(url, retryInit).then(resolve).catch(reject);
                });
            });
        }

        isRefreshing = true;
        try {
            const newToken = await refreshAccessToken();
            if (newToken) {
                onTokenRefreshed(newToken);

                const newHeaders = {
                    ...(options.headers || {}),
                    Authorization: `Bearer ${newToken}`,
                };
                const retryInit = { ...options, headers: newHeaders };
                if (typeof retryInit.credentials === 'undefined') {
                    retryInit.credentials = 'include';
                }
                response = await fetch(url, retryInit);
            } else {
                onTokenRefreshed(null);
                await clearStaleSession();
                redirectToLogin();
            }
        } catch (_) {
            onTokenRefreshed(null);
            await clearStaleSession();
            redirectToLogin();
        } finally {
            isRefreshing = false;
        }
    }

    return response;
};

export default {
    getAuthToken,
    setAccessToken,
    clearAccessToken,
    clearTokens,
    clearStaleSession,
    getAuthHeader,
    fetchWithAuth,
};
